import React, {useMemo} from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import useDocumentSaveState from 'notebook/documents/useDocumentSaveState';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import notifications from 'notifications';
import {
  RequestV2DefinitionPageQuery,
  RequestV2DefinitionPageQueryVariables,
} from 'processes/requests_v2/request-v2-definition/__generated/RequestV2DefinitionPageQuery';
import RequestV2DefinitionHeader from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionHeader';
import {
  REQUEST_V2_DEFINITION_PAGE_QUERY,
  RequestV2DefinitionDetails,
} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionPageQueryUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {typeToName} from 'util/NameUtil';
interface QueryingRequestV2DefinitionPageProps {
  requestV2DefinitionId: string;
}

interface RequestV2DefinitionPageProps {
  documentId: string;
  requestV2Definition: RequestV2DefinitionDetails;
}

function renderRequestV2DefinitionBanner(
  isLockedByPermissions: boolean,
  isWritable: boolean,
  isLatestVersionOpen: boolean,
  isRequestV2DefinitionArchived: boolean
): JSX.Element | null {
  if (isRequestV2DefinitionArchived) {
    return <>Editing is disabled on archived request templates.</>;
  } else if (!isLatestVersionOpen) {
    return <>Editing disabled on previous versions.</>;
  } else if (!isWritable || isLockedByPermissions) {
    return <>You have insufficient permissions to edit this request template.</>;
  }
  return null;
}

export default function QueryingRequestV2DefinitionPage(
  props: QueryingRequestV2DefinitionPageProps
): JSX.Element | null {
  const result = useBenchlingQuery<RequestV2DefinitionPageQuery, RequestV2DefinitionPageQueryVariables>(
    REQUEST_V2_DEFINITION_PAGE_QUERY,
    {
      variables: {requestV2DefinitionId: props.requestV2DefinitionId},
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR || isInaccessibleInfo(result.data.requestV2Definition)) {
    notifications.error('Could not load request template; try reloading the page.');
    return null;
  }

  return (
    <RequestV2DefinitionPage
      documentId={result.data.requestV2Definition.templateDocument.id}
      requestV2Definition={result.data.requestV2Definition}
    />
  );
}

function RequestV2DefinitionPage(props: RequestV2DefinitionPageProps): JSX.Element {
  const isArchived = props.requestV2Definition.archiveData?.archived ?? false;
  const isRequestTemplateReadOnly =
    !props.requestV2Definition.permissions.writable ||
    isArchived ||
    !isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_ADMIN});

  const structuredTableInsertionContextValue = useMemo(
    () => ({itemInsertedFromId: props.requestV2Definition.id}),
    [props.requestV2Definition.id]
  );

  const {latestVersionId, openVersionId} = useDocumentSaveState(props.documentId);
  const isLatestVersionOpen = latestVersionId && openVersionId ? latestVersionId === openVersionId : false;

  return (
    <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
      <RequestV2DefinitionHeader
        isReadOnly={isRequestTemplateReadOnly}
        requestV2Definition={props.requestV2Definition}
      />
      <DocumentEditor
        renderBanner={(isLockedByPermissions: boolean) =>
          renderRequestV2DefinitionBanner(
            isLockedByPermissions,
            props.requestV2Definition.permissions.writable,
            isLatestVersionOpen,
            isArchived
          )
        }
        id={props.documentId}
        title={props.requestV2Definition.name}
        hideCanvasTitle={true}
        hideTitleInDocument={true}
        isVirtualizationEnabled={true}
        locked={isRequestTemplateReadOnly}
        fileTypeName={typeToName(ModelTypeConstants.REQUEST_V2_DEFINITION)}
        manageRealtimeUpdates={true}
        disablePerformanceIndicator={true}
      />
    </StructuredTableInsertionContext.Provider>
  );
}
